import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { Typography } from "storybook/components/typography";
import { Notification } from "storybook/components/notification";
import { ITHACA_HOSTNAME } from "env-vars";
import { Loading } from "components/loading";
import { RiskSummary } from "../../../components/risk-summary";
import { useRiskReport } from "context/risk-report";
import { createSurveySession } from "helpers/create-survey-session";
import { openQuestionnaire } from "helpers";
import { useFeatureFlags } from "context/admin-content";
import { useTransifexLoader } from "context/transifex-loader";
import { DoctorIcon } from "components/icons/doctor";
import {
  Container,
  PromptContainer,
  HeadingContainer,
  SummaryContainer,
  NotificationContainer,
  GraphicContainer,
} from "./styles";

export function RescreenLanding() {
  const {
    enableRescreenAndResults,
    rescreenWithQuestionnaireEnabled,
    enableInternalQuestionnaire,
  } = useFeatureFlags();
  const history = useHistory();
  const { lang, isLoaded: txLangLoaded } = useTransifexLoader();
  const [error, setError] = useState<boolean>(false);
  const [isWaiting, setWaiting] = useState<boolean>(false);
  const {
    isLoading,
    error: reportError,
    hasUpdatePendingApproval,
  } = useRiskReport();

  const handleReviewClick = useCallback(async () => {
    setError(false);
    setWaiting(true);

    const patientId = localStorage.getItem("ciq_id");

    const currentUrl = document.location.href.split("?")[0];

    try {
      const response = await createSurveySession({
        isInitial: false,
        isRemote: true,
      });

      if (!response.ok) {
        console.error("Error initializing survey session");
        setError(true);

        return;
      }

      const json = await response.json();

      if (!json.code) {
        setError(true);

        return;
      }

      // Remove the language prompt flag so that it is not shown again once they return
      // back to the patient portal
      localStorage.removeItem("ciq_can_show_language_prompt");

      const locale = txLangLoaded ? lang : "en";

      if (rescreenWithQuestionnaireEnabled && json.survey_token) {
        if (enableInternalQuestionnaire) {
          const locale = txLangLoaded ? lang : "en";
          history.push(`/q/${json.survey_token}?locale=${locale}`);
        } else {
          const redirectUrl = `${currentUrl}complete`;
          const queryParams = {
            redirectUrl: encodeURIComponent(redirectUrl),
            restartUrl: encodeURIComponent(currentUrl),
            ...(locale ? { locale: locale as string } : {}),
          };

          localStorage.setItem("surveyCode", json.code);

          openQuestionnaire({
            token: json.survey_token,
            context: "remote",
            queryParams,
            newTab: false,
            skipLandingPage: true,
          });
        }
      } else {
        // Default flow: redirect to Ithaca rescreen wizard
        const params = new URLSearchParams();
        params.set("code", json.code);
        params.set("locale", locale as string);
        params.set("referrer", currentUrl);

        window.location.assign(
          `${ITHACA_HOSTNAME}/patients/${patientId}/encounters/${patientId}/rescreen-questionnaire?${params.toString()}`
        );
      }
    } catch (error) {
      console.error("Error initializing survey session", error);
      setError(true);
    } finally {
      setWaiting(false);
    }
  }, [lang, txLangLoaded, rescreenWithQuestionnaireEnabled, enableInternalQuestionnaire, history]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Container>
      {!hasUpdatePendingApproval && (
        <HeadingContainer>
          <Typography
            variant="h5"
            component="h1"
            theme="default"
            color="ciqBrandDark"
          >
            You can update your information
          </Typography>
        </HeadingContainer>
      )}
      {enableRescreenAndResults ? (
        <>
          {error && (
            <NotificationContainer>
              <Notification
                fullWidth
                theme="default"
                variant="warning"
                title="Error loading rescreen, please try again."
              />
            </NotificationContainer>
          )}
          <SummaryContainer>
            <RiskSummary
              stacked
              error={reportError}
              handleReviewClick={handleReviewClick}
              isWaiting={isWaiting}
            />
          </SummaryContainer>
        </>
      ) : (
        <>
          <PromptContainer>
            <Typography variant="body1" theme="default" color="ciqBrandDark">
              To provide or update any additional information please contact
              your provider.
            </Typography>
          </PromptContainer>
          <GraphicContainer>
            <DoctorIcon />
          </GraphicContainer>
        </>
      )}
    </Container>
  );
}
